import type { TrackingCookie, TrackingCookieKey } from "../types/trackingEntry";

import { TrackingCookieKeys } from "../types/trackingEntry";

// Create a Set of descriptive keys for efficient lookup
const descriptiveKeys = new Set(TrackingCookieKeys as readonly string[]);

/**
 * Parses UTM parameters from a URL's search string.
 * @param searchString The URL's search string (e.g., "?utm_source=google&utm_medium=cpc").
 * @returns An object containing the parsed UTM parameters.
 */
export function parseUtmParameters(searchString: string): TrackingCookie {
  const params: TrackingCookie = {};
  const queryString = searchString.startsWith("?")
    ? searchString.substring(1)
    : searchString;
  const pairs = queryString.split("&");

  for (const pair of pairs) {
    const [key, value] = pair.split("=");
    if (key && value) {
      const decodedKey = decodeURIComponent(key);
      const decodedValue = decodeURIComponent(value);

      if (descriptiveKeys.has(decodedKey)) {
        params[decodedKey as TrackingCookieKey] = decodedValue;
      }
    }
  }
  return params;
}

/**
 * Converts UTM parameters into a TrackingCookie array.
 * @param utmParams An object of UTM parameters (e.g., { utm_source: 'google', utm_medium: 'cpc' }).
 * @returns An array representing the TrackingCookie.
 */
export function utmParamsToTrackingCookie(
  utmParams: Record<string, string>,
): TrackingCookie[] {
  const trackingEntry: TrackingCookie = {};

  for (const key in utmParams) {
    if (descriptiveKeys.has(key as TrackingCookieKey)) {
      trackingEntry[key as TrackingCookieKey] = utmParams[key];
    }
  }

  return [trackingEntry];
}

/**
 * Converts a TrackingCookie array back into a simplified key-value object for easier encoding.
 * @param trackingCookieArray An array representing the TrackingCookie.
 * @returns An object where keys are the TrackingCookie keys and values are their corresponding values.
 */
export function trackingCookieToKeyValue(
  trackingCookieArray: TrackingCookie[],
): Record<string, string> {
  const keyValue: Record<string, string> = {};
  if (trackingCookieArray && trackingCookieArray.length > 0) {
    const trackingCookie = trackingCookieArray[0];
    for (const key in trackingCookie) {
      if (Object.hasOwn(trackingCookie, key)) {
        keyValue[key] = trackingCookie[key as TrackingCookieKey]!;
      }
    }
  }
  return keyValue;
}

/**
 * Obfuscates the cookie value using the same algorithm as the C# Obfuscate extension method
 * URL token encoding after XOR encryption with a key
 * @param clearValue The string to be obfuscated.
 * @returns The obfuscated string.
 */
export function obfuscate(clearValue: string): string {
  // The ObfuscationKey from the C# backend
  const obfuscationKey = "0F75ED5B-02CE-4CBE-BAFC-85076C8692BC";

  // Convert the clear value to UTF-8 bytes
  const encoder = new TextEncoder();
  const bytesToEncrypt = encoder.encode(clearValue);

  // Convert the key to UTF-8 bytes
  const encryptionKeyBytes = encoder.encode(obfuscationKey);

  // XOR encryption
  for (let i = 0; i < bytesToEncrypt.length; i++) {
    bytesToEncrypt[i] ^= encryptionKeyBytes[i % encryptionKeyBytes.length];
  }

  // Equivalent to C#'s UrlTokenEncode
  return urlTokenEncode(bytesToEncrypt);
}

function urlTokenEncode(input: Uint8Array): string {
  if (!input || input.length === 0) {
    return "";
  }

  const base64 = btoa(String.fromCharCode(...input));

  // Remove trailing '=' padding
  let trimmedBase64 = base64.replace(/=+$/, "");

  // Replace '+' with '-' and '/' with '_'
  trimmedBase64 = trimmedBase64.replace(/\+/g, "-").replace(/\//g, "_");

  // Add the number of removed padding characters as the last char
  const paddingLength = base64.length - trimmedBase64.length;

  return trimmedBase64 + paddingLength.toString(10);
}

/**
 * Deobfuscates the cookie value using the same algorithm as the C# Deobfuscate extension method
 * XOR decryption with a key after URL token decoding
 * @param obfuscatedValue The obfuscated cookie value
 * @returns The deobfuscated string
 */
export function deobfuscate(obfuscatedValue: string): string {
  // The ObfuscationKey from the C# backend
  const obfuscationKey = "0F75ED5B-02CE-4CBE-BAFC-85076C8692BC";

  // Equivalent to C#'s UrlTokenDecode
  const bytesToDecrypt = urlTokenDecode(obfuscatedValue);

  // Convert the key to UTF-8 bytes
  const encoder = new TextEncoder();
  const encryptionKeyBytes = encoder.encode(obfuscationKey);

  // XOR decryption
  for (let i = 0; i < bytesToDecrypt.length; i++) {
    bytesToDecrypt[i] ^= encryptionKeyBytes[i % encryptionKeyBytes.length];
  }

  // Convert decrypted bytes back to string
  const decoder = new TextDecoder("utf-8");
  return decoder.decode(bytesToDecrypt);
}

function urlTokenDecode(input: string): Uint8Array {
  if (!input || input.length < 1) {
    return new Uint8Array();
  }

  // Get the last char — it indicates how many padding characters were stripped
  const padChar = input[input.length - 1];
  const numPadChars = parseInt(padChar, 10);

  if (isNaN(numPadChars) || numPadChars < 0 || numPadChars > 10) {
    throw new Error("Invalid padding length indicator in encoded token");
  }

  // Base64 input (minus the trailing padding indicator char)
  const base64Body = input
    .substring(0, input.length - 1)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  // Reconstruct original padded base64
  const paddedBase64 = base64Body + "=".repeat(numPadChars);

  const binaryString = atob(paddedBase64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes;
}

export async function createTrackingCookie({
  trackingCookie: inputTrackingCookie,
}: {
  trackingCookie: TrackingCookie[];
}) {
  const now = new Date().toISOString();
  const trackingCookieWithTimestamp: TrackingCookie[] = inputTrackingCookie.map(
    (cookie) => ({ ...cookie, timestamp: now }),
  );
  const stringifiedCookie = JSON.stringify(trackingCookieWithTimestamp);
  const obfuscatedValue = obfuscate(stringifiedCookie);
  return obfuscatedValue;
}
